<template lang="pug">
div
  div.d-flex.align-center
    div.text-xs.font-medium.neutral700--text Upcoming appointments
    v-spacer
    doctor-filter-dropdown.ml-3(@change="fetchFirstPage" type="appointmet")
    y-button.ml-3(label="Create new appointment" unset-min-width size="xxs" button-class="px-2" height="24" @click="onSetAppointment")

  template(v-if="!appointments.length && !isFetching")
    div.d-flex.align-center.justify-center.fill-height.round-xl.mt-4(style="border: 1px dashed var(--v-neutral400); min-height: 180px;")
      div(align="center")
        v-icon(size="32" color="neutral500") $yzi-calendar
        div.text-sm.font-medium.neutral700--text.mt-2 No upcoming appointments
        div.text-sm.neutral500--text.mt-1 Created appointment will appear here
        y-button.mt-2(label="Add appointment" color="neutral300" text-color="neutral700" prepend-icon="mdi-plus" icon-size="16" icon-color="neutral500" unset-min-width size="xs" button-class="px-2" @click="onSetAppointment")
  v-card.mt-3(v-else="appointments" outlined)
    .data-table
      v-data-table.table-custom(:headers="headers" :items="appointments" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" dense disable-sort :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2")
        //- providers
        template(v-slot:item.provider="{ item, headers, index }")
          div(:id="`pv-${index}`" style="position: relative;")
          div.my-3(v-if="item.provider && Object.keys(item.provider).length")
            v-tooltip(top color="black" :attach="`#pv-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  div.d-flex.align-center
                    div.mr-2.no-wrap
                      template(v-if="item.type === 'combo'")
                        template(v-for="(set, idx) in item.combo_set")
                          v-avatar(size="20" color="neutral300" :class="idx > 0 ? 'stack-image' : ''" :key="set.provider.id")
                            template(v-if="set.provider.profile_image")
                              v-img(:src="`https://img.yezza.io/50x50/${set.provider.profile_image.path}`")
                            v-icon(v-else color="neutral500" size="15") $yzi-user-03
                      v-avatar(v-else size="20" color="neutral300")
                        template(v-if="item.provider.profile_image")
                          v-img(:src="`https://img.yezza.io/50x50/${item.provider.profile_image.path}`")
                        v-icon(v-else color="neutral500" size="15") $yzi-user-03
                    div.text-xs.font-medium.neutral700--text.no-wrap {{ getProviderName(item) }}
                        
              template(v-if="item.type === 'combo' && item.combo_set.length > 1")
                div(v-for="(set, idx) in item.combo_set" :key="`ttpv-${index}-${idx}`") {{ idx + 1 }}. {{ set.provider.name }}
              div(v-else) {{ item.provider.name }}
        //- datetime
        template(v-slot:item.datetime="{ item, index }")
          div(:id="`st-${index}`" style="position: relative;")
          div.my-3
            v-tooltip(top color="black" :attach="`#st-${index}`" min-width="340" max-width="500")
              template(#activator="{ on, attrs }")
                div.no-wrap(v-bind="attrs" v-on="on")
                  div.text-xs.font-medium(v-if="item.start") {{ item.start | humanizeDate }}
                  div.d-flex.align-center(v-if="item.time_range && item.time_range.length")
                    div.text-xs {{ item.time_range[0] | displayTime2 }} - {{ item.time_range[1] | displayTime2 }} 
              div.py-2
                div.text-xs.font-semibold Scheduled time
                div.mt-1(v-if="item.type === 'combo'")
                  template(v-for="(set, idx) in item.combo_set")
                    v-divider.neutral100.my-2(v-if="idx")
                    v-row(dense :key="set.id+set.pid")
                      v-col(cols="6")
                        div.d-flex.align-center(v-if="set.start")
                          div.text-xs {{ set.start | displayTime2 }}
                          div.text-xs.ml-1(v-if="set.until") - {{ set.until | displayTime2 }} 
                      v-col(cols="6")
                        div.text-xs(v-if="set.services.length") {{ set.services.map(m => m.name).join(', ') }}
                        div.text-xxs.mt-1 {{ set.provider.name }}
                v-row.mt-1(v-else dense)
                  v-col.pr-1(cols="6")
                    div.d-flex.align-center(v-if="item.start")
                      div.text-xs {{ item.start | displayTime2 }}
                      div.text-xs.ml-1(v-if="item.until") - {{ item.until | displayTime2 }} 
                  v-col.pl-1(cols="6")
                    div.text-xs(v-if="item.services.length") {{ item.services.map(m => m.name).join(', ') }}
                    div.text-xxs.mt-1 {{ item.provider.name }}
        //- type
        template(v-slot:item.type="{ item }")
          y-chip.no-wrap(v-if="item.type === 'regular'" label="Normal" color="normal" size="xs")
          y-chip.no-wrap(v-if="item.type === 'recur'" label="Recurring" color="blue" size="xs")
            template(#prepend)
              v-icon.mr-1(color="info500" size="12") $yzi-refresh-cw-02
          y-chip.no-wrap(v-if="item.type === 'combo'" label="Multi-service" color="orange" size="xs")
            template(#prepend)
              v-icon.mr-1(color="yellow600" size="12") $yzi-users-03
        //- service
        template(v-slot:item.services="{ item, headers, index }")
          div(:id="`sv-${index}`" style="position: relative;")
          div.my-3
            v-tooltip(top color="black" :attach="`#sv-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  template(v-if="item.type === 'combo'")
                    template(v-for="(set, idx) in item.combo_set")
                      div.d-flex.align-center(v-if="idx < 2" :key="`comb-${index}-${idx}`")
                        div.text-xs.ellipsis-text-1(v-if="set.services.length") {{ set.services[0].name }}
                        div.text-xs(v-else) -
                        template(v-if="idx === 1 && item.combo_set.length > 2")
                          v-spacer
                          div.text-xs.font-medium +{{ item.combo_set.length - 2 }} more
                  template(v-else)
                    template(v-for="(service, idx) in item.services")
                      div.d-flex.align-center(v-if="idx < 2" :key="`serv-${index}-${idx}`")
                        div.text-xs.ellipsis-text-1 {{ service.name }}
                        template(v-if="idx === 1 && item.services.length > 2")
                          v-spacer
                          div.text-xs.font-medium +{{ item.services.length - 2 }} more
              template(v-if="item.type === 'combo'")
                template(v-for="(set, idx) in item.combo_set")
                  div(:key="`ttcs-${index}-${idx}`") {{ idx + 1 }}. {{ set.services[0].name }}
              div(v-else)
                template(v-for="(service, idx) in item.services")
                  div(:key="`ttsv-${index}-${idx}`") {{ idx + 1 }}. {{ service.name }}
        //- payment
        template(v-slot:item.payment="{ item, headers, index }")
          div.d-flex.align-center
            div.text-xs.font-medium.neutral700--text.mr-1.no-wrap(v-if="getBookingFees(item.services)") {{ getBookingFees(item.services) | displayPrice($currency) }}
            y-chip(v-if="item.paid === null"  label="N/A" color="green" size="xs")
            y-chip(v-else-if="item.paid"  label="Paid" color="green" size="xs")
            y-chip(v-else label="Unpaid" color="red" size="xs")
        //- actions
        template(v-slot:item.actions="{ item, headers, index }")
          div.d-flex.align-center.justify-end.py-3(v-if="member.provider === item.provider.id" @click.stop)
            v-menu(offset-x left rounded="lg")
              template(#activator="{ on }")
                v-btn.ml-2(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0
                v-list-item(@click="openRescheduleDate(item)")
                  v-list-item-title.text-sm.neutral700--text Reschedule
                v-divider
                v-list-item(@click="openCancelAppointment(item)")
                  v-list-item-title.text-sm.danger500--text Cancel appointment

      div.d-flex.align-center.px-3.pb-2(v-if="totalCount > pageSize")
        v-spacer
        div
          yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  .dialogs
    set-appointment-dialog(v-if="showSetAppointmentDialog" :open.sync="showSetAppointmentDialog" :patient="patient" @advance="openAdvanceAppointment" @closed="fetchAppointments")
    add-advance-appointment(v-if="showAdvAppointmentDialog" :open.sync="showAdvAppointmentDialog" :patient="patient" @reset="fetchAppointments")
    reschedule-date(v-if="showRescheduleDateDialog" :open.sync="showRescheduleDateDialog" :selectedAppointment="selectedAppointment" @closed="selectedAppointment = null" @reset="fetchAppointments")
    cancel-appointment(v-if="showCancelAppointmentDialog" :open.sync="showCancelAppointmentDialog" :selectedAppointment="selectedAppointment" @closed="selectedAppointment = null" @reset="fetchAppointments")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import SetAppointmentDialog from '@/components/clinic/dialogs/SetAppointmentDialog.vue'
import AddAdvanceAppointment from '@/components/clinic/dialogs/AddAdvanceAppointment.vue'
import RescheduleDate from '@/components/clinic/dialogs/RescheduleDate.vue'
import CancelAppointment from '@/components/clinic/dialogs/CancelAppointment.vue'
import DoctorFilterDropdown from '@/components/clinic/consultation/DoctorFilterDropdown.vue'

export default {
  components: { SetAppointmentDialog, AddAdvanceAppointment, RescheduleDate, CancelAppointment, DoctorFilterDropdown },
  props: {},
  data () {
    return {
      isFetching: false,
      showSetAppointmentDialog: false,
      showAdvAppointmentDialog: false,
      showRescheduleDateDialog: false,
      showCancelAppointmentDialog: false,
      selectedAppointment: null,
      pageSize: 20,
      totalCount: 0,
      currentPage: 1,
      appointments: [],
      headers: [
        { text: 'Providers', value: 'provider' },
        { text: 'Date & time', value: 'datetime' },
        { text: 'Appt type', value: 'type' },
        { text: 'Services', value: 'services' },
        { text: 'Payment', value: 'payment' },
        { text: '', value: 'actions' },
      ],
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      patient: 'treatment/getPatient',
      pid: 'treatment/getLeadPid',
      appointmentFilters: 'treatment/getAppointmentFilters',
    }),
    doctors () {
      let ids = null
      if (this.appointmentFilters.doctors) {
        ids = this.appointmentFilters.doctors.map(m => m.value).join(',')
      }
      return ids
    },
  },
  mounted () {
    this.fetchAppointments()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointments () {
      this.isFetching = true
      this.selectedAppointment = null
      let params = {
        page_size: this.pageSize,
        patient: this.patient.id,
        statuses: 'new,processed',
        ordering: 'start',
        providers: this.doctors,
      }
      this.$axios.get(`/clinics/${this.company.slug}/appointments`, { params })
        .then(resp => {
          this.appointments = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchFirstPage () {
      this.currentPage = 1
      this.fetchAppointments()
    },
    pageChanged () {
      this.fetchAppointments()
    },
    getProviderName (item) {
      let str = ''
      if (item.type === 'combo' && item.combo_set.length > 1) {
        str = item.combo_set.length + ' providers'
      } else {
        str = item.provider.name
        if (str.length > 15) {
          str = str.substring(0, 15 - 3) + '...';
        }
      }
      return str
    },
    getBookingFees (services) {
      let fee = services.map(m => m.price).reduce((a, b) => a + b, 0)
      return fee
    },
    onSetAppointment () {
      this.showSetAppointmentDialog = true
    },
    openAdvanceAppointment () {
      this.showSetAppointmentDialog = false
      this.showAdvAppointmentDialog = true
    },
    openRescheduleDate (item) {
      this.selectedAppointment = item
      this.showRescheduleDateDialog = true
    },
    openCancelAppointment (item) {
      this.selectedAppointment = item
      this.showCancelAppointmentDialog = true
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-data-table__wrapper table tbody tr:hover{
    cursor: pointer;
    background: var(--v-neutral200) !important; 
  }
  .table-custom {
    .v-data-table-header {
      background-color: var(--v-neutral200);
    }
    .text-table-header, .v-data-table__wrapper > table > thead > tr > th {
      text-transform: none !important;
    }
    .v-data-table__wrapper {
      overflow: visible !important;
    }
    thead tr th {
      padding: 0px 12px !important;
    }
    tbody tr td {
      padding: 0px 12px !important;
    }
  }
}
.stack-image {
  margin-left: -13px;
}
.v-tooltip__content {
  top: unset !important;
  left: unset !important;
  position: absolute;
  bottom: 0;
}
</style>